import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { getImageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Send, ArrowLeft, MessageSquare, Paperclip, ImagePlus, X,
  Check, CheckCheck, File as FileIcon, Download, PenSquare, Search, Users as UsersIcon
} from "lucide-react";
import { toast } from "sonner";

const ATTACH_ACCEPT = "image/jpeg,image/jpg,image/png,image/gif,image/webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const IMAGE_ACCEPT = "image/jpeg,image/jpg,image/png,image/gif,image/webp";
const MAX_ATTACH_BYTES = 10 * 1024 * 1024;

function formatLastActive(iso, online) {
  if (online) return "Active now";
  if (!iso) return "Offline";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Active just now";
  if (mins < 60) return `Active ${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Active ${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `Active ${days}d ago`;
  return `Active ${d.toLocaleDateString()}`;
}

function ComposeDialog({ open, onClose, existingIds = [], onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [connectionsOnly, setConnectionsOnly] = useState(true);
  const [connIds, setConnIds] = useState(null); // null = not loaded, [] = none

  useEffect(() => {
    if (!open) { setQ(""); setResults([]); return; }
    let active = true;
    api.get("/connections").then(res => {
      if (!active) return;
      const ids = new Set();
      (res.data || []).forEach(c => {
        if (c.status === "accepted") {
          const other = c.other_user?._id || c.other_user?.id;
          if (other) ids.add(other);
        }
      });
      setConnIds(Array.from(ids));
    }).catch(() => { if (active) setConnIds([]); });
    return () => { active = false; };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        if (q.trim()) params.set("search", q.trim());
        params.set("limit", 20);
        const { data } = await api.get(`/scout/discover?${params.toString()}`);
        let list = (data.users || []).filter(u => !existingIds.includes(u._id));
        if (connectionsOnly && Array.isArray(connIds)) {
          list = list.filter(u => connIds.includes(u._id));
        }
        if (active) setResults(list);
      } catch { /* ignore */ }
      finally { if (active) setLoading(false); }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [open, q, connectionsOnly, connIds, existingIds]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="compose-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <PenSquare size={16} className="text-[#074572]" /> New Message
          </DialogTitle>
        </DialogHeader>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search people by name, role, position..."
            className="pl-9 border-[#E6E6E6]"
            autoFocus
            data-testid="compose-search-input"
          />
        </div>
        <div className="flex items-center gap-3 border-b border-[#E6E6E6] pb-2">
          <button
            type="button"
            onClick={() => setConnectionsOnly(true)}
            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${connectionsOnly ? "bg-[#074572] text-white border-[#074572]" : "bg-white text-zinc-500 border-[#E6E6E6] hover:border-[#074572]/40"}`}
            data-testid="compose-scope-connections"
          >
            <UsersIcon size={11} className="inline mr-1" /> My connections
          </button>
          <button
            type="button"
            onClick={() => setConnectionsOnly(false)}
            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${!connectionsOnly ? "bg-[#074572] text-white border-[#074572]" : "bg-white text-zinc-500 border-[#E6E6E6] hover:border-[#074572]/40"}`}
            data-testid="compose-scope-all"
          >
            Everyone
          </button>
        </div>
        <div className="max-h-72 overflow-y-auto -mx-1">
          {loading && results.length === 0 ? (
            <p className="text-center py-4 text-xs text-zinc-400">Loading...</p>
          ) : results.length === 0 ? (
            <div className="text-center py-6 text-xs text-zinc-400">
              <MessageSquare size={22} className="mx-auto mb-2 opacity-50" />
              <p>{connectionsOnly && (connIds?.length === 0) ? "You have no connections yet." : "No people match your search."}</p>
              {connectionsOnly && (
                <button onClick={() => setConnectionsOnly(false)} className="text-[#074572] font-medium hover:underline mt-1">
                  Search everyone
                </button>
              )}
            </div>
          ) : (
            results.map(u => {
              const initials = u.name?.split(" ").map(n => n[0]).join("").toUpperCase() || "?";
              const avatar = getImageUrl(u.profile_image);
              return (
                <button
                  key={u._id}
                  type="button"
                  onClick={() => onPick(u)}
                  className="w-full text-left px-3 py-2 hover:bg-zinc-50 rounded-md flex items-center gap-2"
                  data-testid={`compose-pick-${u._id}`}
                >
                  <Avatar className="h-9 w-9 shrink-0 border border-[#E6E6E6]">
                    {avatar ? <AvatarImage src={avatar} /> : null}
                    <AvatarFallback className="bg-[#074572] text-white text-[10px]">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-zinc-900 truncate">{u.name}</p>
                    <p className="text-[11px] text-zinc-500 truncate capitalize">
                      {u.role}{u.position ? ` · ${u.position}` : ""}{u.location ? ` · ${u.location}` : ""}
                    </p>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AttachmentPreview({ msg }) {  if (!msg.attachment_url) return null;
  const src = msg.attachment_url.startsWith("http") ? msg.attachment_url : getImageUrl(msg.attachment_url);
  if (msg.attachment_type === "image") {
    return (
      <a href={src} target="_blank" rel="noopener noreferrer" className="block mt-2">
        <img src={src} alt={msg.attachment_name || "attachment"} className="max-h-64 rounded-md border border-white/20" />
      </a>
    );
  }
  return (
    <a
      href={src}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 mt-2 px-3 py-2 bg-black/5 border border-black/10 rounded-md hover:bg-black/10 transition-colors"
      data-testid={`message-attachment-${msg.id}`}
    >
      <FileIcon size={16} className="shrink-0" />
      <span className="text-xs truncate flex-1">{msg.attachment_name || "File"}</span>
      <Download size={13} className="shrink-0 opacity-70" />
    </a>
  );
}

export default function MessagesPage() {
  const { userId: directUserId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [presenceMap, setPresenceMap] = useState({}); // {userId: {online, last_active}}
  const [pendingAttach, setPendingAttach] = useState(null); // {url, name, type, size, previewUrl}
  const [uploading, setUploading] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [parentalLock, setParentalLock] = useState(null); // {locked, count}
  const navigate2 = useNavigate();

  useEffect(() => {
    api.get("/parental/pending-preview").then(r => setParentalLock(r.data)).catch(() => setParentalLock(null));
  }, []);
  const messagesEnd = useRef(null);
  const wsRef = useRef(null);
  const pollRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const myId = user?._id || user?.id;
  const activeConvIdRef = useRef(null);
  activeConvIdRef.current = activeConv?.conversation_id || null;

  // WebSocket connection
  useEffect(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const wsUrl = backendUrl.replace('https://', 'wss://').replace('http://', 'ws://') + '/api/ws';

    const connectWs = () => {
      try {
        const ws = new WebSocket(wsUrl);
        ws.onopen = () => {
          setWsConnected(true);
          ws.send(JSON.stringify({ type: "ping" }));
        };
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === "new_message") {
              // Only append if it belongs to the currently open conversation
              if (activeConvIdRef.current && data.conversation_id === activeConvIdRef.current) {
                setMessages(prev => {
                  if (prev.some(m => m.id === data.id)) return prev;
                  return [...prev, data];
                });
              }
              fetchConversations();
            } else if (data.type === "presence") {
              setPresenceMap(prev => ({
                ...prev,
                [data.user_id]: { online: data.online, last_active: data.ts },
              }));
            } else if (data.type === "seen") {
              if (activeConvIdRef.current === data.conversation_id) {
                setMessages(prev => prev.map(m => (
                  data.message_ids?.includes(m.id) ? { ...m, read: true, seen_at: data.seen_at } : m
                )));
              }
            }
          } catch (err) { console.warn("WS message parse failed:", err); }
        };
        ws.onclose = () => {
          setWsConnected(false);
          setTimeout(connectWs, 5000);
        };
        ws.onerror = () => { setWsConnected(false); };
        wsRef.current = ws;
      } catch {
        setWsConnected(false);
      }
    };

    connectWs();
    return () => {
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  const fetchConversations = useCallback(async () => {
    try {
      const { data } = await api.get("/messages/conversations");
      setConversations(data);
      // Seed presence map with online flags from conversations
      setPresenceMap(prev => {
        const next = { ...prev };
        data.forEach(c => {
          const uid = c.other_user?._id;
          if (uid) {
            next[uid] = {
              online: !!c.other_user.online,
              last_active: c.other_user.last_active || next[uid]?.last_active,
            };
          }
        });
        return next;
      });
      return data;
    } catch { return []; }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const convs = await fetchConversations();
      if (directUserId && myId) {
        const sorted = [myId, directUserId].sort();
        const convId = `${sorted[0]}_${sorted[1]}`;
        const existing = convs.find(c => c.conversation_id === convId);
        if (existing) {
          setActiveConv(existing);
        } else {
          try {
            const { data: otherUser } = await api.get(`/users/${directUserId}`);
            setActiveConv({ conversation_id: convId, other_user: otherUser, last_message: null, unread_count: 0 });
          } catch (err) { console.warn("Could not fetch target user for direct conv:", err); }
        }
      }
      setLoading(false);
    };
    init();
  }, [directUserId, myId, fetchConversations]);

  // Fetch presence for all conversation partners periodically (fallback when WS silent)
  useEffect(() => {
    const partnerIds = conversations.map(c => c.other_user?._id).filter(Boolean);
    if (activeConv?.other_user?._id) partnerIds.push(activeConv.other_user._id);
    const uniq = Array.from(new Set(partnerIds));
    if (uniq.length === 0) return undefined;

    const fetchPresence = async () => {
      try {
        const { data } = await api.post("/messages/presence", { user_ids: uniq });
        setPresenceMap(prev => {
          const next = { ...prev };
          (data || []).forEach(p => {
            next[p.user_id] = { online: !!p.online, last_active: p.last_active };
          });
          return next;
        });
      } catch { /* presence check silent */ }
    };
    fetchPresence();
    const t = setInterval(fetchPresence, 30000);
    return () => clearInterval(t);
  }, [conversations, activeConv?.other_user?._id]);

  const fetchMessages = useCallback(async (convId) => {
    try {
      const { data } = await api.get(`/messages/${convId}`);
      setMessages(data);
    } catch (err) { console.warn("Failed to fetch messages:", err); }
  }, []);

  useEffect(() => {
    if (activeConv?.conversation_id) {
      fetchMessages(activeConv.conversation_id);
      // Only poll if WebSocket is not connected
      if (pollRef.current) clearInterval(pollRef.current);
      if (!wsConnected) {
        pollRef.current = setInterval(() => fetchMessages(activeConv.conversation_id), 5000);
      }
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeConv?.conversation_id, fetchMessages, wsConnected]);

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handlePickFile = (accept) => {
    if (accept === IMAGE_ACCEPT) imageInputRef.current?.click();
    else fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_ATTACH_BYTES) {
      toast.error("File exceeds 10MB limit");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/messages/attachment-upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const previewUrl = data.type === "image" && file.type.startsWith("image/")
        ? URL.createObjectURL(file)
        : null;
      setPendingAttach({
        url: data.url,
        name: data.name,
        type: data.type,
        size: data.size,
        previewUrl,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload attachment");
    } finally {
      setUploading(false);
    }
  };

  const clearPendingAttach = () => {
    if (pendingAttach?.previewUrl) URL.revokeObjectURL(pendingAttach.previewUrl);
    setPendingAttach(null);
  };

  const handleSend = async () => {
    if ((!newMsg.trim() && !pendingAttach) || !activeConv) return;
    const receiverId = activeConv.other_user?._id || activeConv.other_user?.id;
    if (!receiverId) return;
    setSending(true);
    try {
      const payload = {
        receiver_id: receiverId,
        content: newMsg.trim() || (pendingAttach ? (pendingAttach.type === "image" ? "📷 Image" : `📎 ${pendingAttach.name}`) : ""),
        attachment_url: pendingAttach?.url || null,
        attachment_name: pendingAttach?.name || null,
        attachment_type: pendingAttach?.type || null,
        attachment_size: pendingAttach?.size || null,
      };
      const { data } = await api.post("/messages", payload);
      setMessages(prev => (data.held ? prev : [...prev, data]));
      setNewMsg("");
      clearPendingAttach();
      if (data.held) {
        toast.info("This new conversation is on hold until a parent/guardian approves it.");
      }
      fetchConversations();
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (err.response?.status === 403 && detail.includes("parental_approval")) {
        toast.warning("Waiting for a parent/guardian to approve this conversation before it can continue.");
      } else if (err.response?.status === 403 && detail.includes("parental_lock")) {
        toast.error("Link a parent/guardian to your account to start messaging.");
      } else {
        toast.error("Failed to send message");
      }
    }
    finally { setSending(false); }
  };

  const otherPresence = useMemo(() => {
    const oid = activeConv?.other_user?._id;
    if (!oid) return { online: false, last_active: null };
    return presenceMap[oid] || {
      online: !!activeConv?.other_user?.online,
      last_active: activeConv?.other_user?.last_active,
    };
  }, [activeConv, presenceMap]);

  if (loading) return <div className="flex items-center justify-center h-[calc(100vh-4rem)] text-zinc-400">Loading...</div>;

  // Parental lock: underage athlete without link — full-page message
  if (parentalLock?.locked) {
    return (
      <div className="max-w-2xl mx-auto py-16 px-4" data-testid="parental-lock-screen">
        <div className="bg-white border border-[#E6E6E6] rounded-xl p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-[#F85037]/10 flex items-center justify-center mx-auto mb-4">
            <MessageSquare size={22} className="text-[#F85037]" />
          </div>
          <h1 className="text-xl font-semibold text-zinc-900" style={{ fontFamily: 'Outfit' }}>Messaging is locked</h1>
          <p className="text-sm text-zinc-600 mt-3">
            You have <span className="font-bold text-[#F85037]" data-testid="parental-pending-count">{parentalLock.count}</span> pending {parentalLock.count === 1 ? "conversation" : "conversations"}.
            Ask a parent to link your account to read and reply to messages.
          </p>
          <p className="text-[11px] text-zinc-400 mt-2">
            For your safety, athletes aged 10–15 need a parent linked to their account before they can use SportsUnity messaging.
          </p>
          <Button
            onClick={() => navigate2("/profile")}
            className="mt-5 bg-[#074572] hover:bg-[#053a60] text-white h-9 text-sm"
            data-testid="parental-lock-goto-profile"
          >
            Go to your profile to link a parent
          </Button>
        </div>
      </div>
    );
  }

  const otherInitials = activeConv?.other_user?.name?.split(" ").map(n => n[0]).join("").toUpperCase() || "?";
  const otherAvatar = getImageUrl(activeConv?.other_user?.profile_image);

  return (
    <div className="max-w-5xl mx-auto h-[calc(100vh-4rem)] flex" data-testid="messages-page">
      {/* Conversations List */}
      <aside className={`w-full md:w-80 border-r border-[#E6E6E6] bg-white shrink-0 ${activeConv ? 'hidden md:block' : 'block'}`}>
        <div className="p-4 border-b border-[#E6E6E6] flex items-center justify-between gap-2">
          <h2 className="text-lg font-medium text-zinc-900" style={{ fontFamily: 'Outfit' }}>Messages</h2>
          <Button
            size="sm"
            onClick={() => setComposeOpen(true)}
            className="bg-[#F85037] hover:bg-[#e04530] text-white h-8 gap-1.5"
            data-testid="messages-compose-btn"
          >
            <PenSquare size={13} /> New
          </Button>
        </div>
        <ScrollArea className="h-[calc(100vh-8rem)]">
          {conversations.length === 0 ? (
            <div className="p-8 text-center text-zinc-400 text-sm">
              <MessageSquare size={32} className="mx-auto mb-2 opacity-50" />
              No conversations yet
            </div>
          ) : (
            conversations.map(conv => {
              const other = conv.other_user;
              const uid = other?._id;
              const presence = presenceMap[uid] || { online: !!other?.online, last_active: other?.last_active };
              const initials = other?.name?.split(" ").map(n => n[0]).join("").toUpperCase() || "?";
              const otherImg = getImageUrl(other?.profile_image);
              const isActive = activeConv?.conversation_id === conv.conversation_id;
              return (
                <button
                  key={conv.conversation_id}
                  onClick={() => setActiveConv(conv)}
                  className={`w-full flex items-center gap-3 p-4 border-b border-[#E6E6E6] text-left transition-colors ${
                    isActive ? "bg-[#074572]/5" : "hover:bg-zinc-50"
                  }`}
                  data-testid={`conv-${conv.conversation_id}`}
                >
                  <div className="relative shrink-0">
                    <Avatar className="h-10 w-10 border border-[#E6E6E6]">
                      {otherImg ? <AvatarImage src={otherImg} /> : null}
                      <AvatarFallback className="bg-[#074572] text-white text-sm">{initials}</AvatarFallback>
                    </Avatar>
                    {presence.online && (
                      <span
                        className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"
                        data-testid={`conv-online-${uid}`}
                        aria-label="Online"
                      />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className={`text-sm truncate ${conv.unread_count > 0 ? "font-bold text-zinc-900" : "font-medium text-zinc-900"}`}>{other?.name}</p>
                      {conv.unread_count > 0 && (
                        <span className="w-5 h-5 bg-[#F85037] text-white text-[10px] rounded-full flex items-center justify-center font-bold shrink-0" data-testid={`conv-unread-${conv.conversation_id}`}>
                          {conv.unread_count}
                        </span>
                      )}
                    </div>
                    <p className={`text-xs truncate mt-0.5 ${conv.unread_count > 0 ? "font-semibold text-zinc-800" : "text-zinc-400"}`}>
                      {conv.last_message?.attachment_url && !conv.last_message?.content ?
                        (conv.last_message.attachment_type === "image" ? "📷 Image" : `📎 ${conv.last_message.attachment_name || "File"}`) :
                        conv.last_message?.content}
                    </p>
                  </div>
                </button>
              );
            })
          )}
        </ScrollArea>
      </aside>

      {/* Chat Area */}
      <div className={`flex-1 flex flex-col bg-[#FAFAFA] ${!activeConv ? 'hidden md:flex' : 'flex'}`}>
        {!activeConv ? (
          <div className="flex-1 flex items-center justify-center text-zinc-400">
            <div className="text-center">
              <MessageSquare size={48} className="mx-auto mb-3 opacity-30" />
              <p className="font-medium">Select a conversation</p>
              <p className="text-sm mt-1">Choose from your existing conversations or start a new one</p>
              <Button
                onClick={() => setComposeOpen(true)}
                className="mt-4 bg-[#F85037] hover:bg-[#e04530] text-white gap-1.5"
                data-testid="messages-empty-compose-btn"
              >
                <PenSquare size={14} /> Start new conversation
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Chat Header */}
            <div className="bg-white border-b border-[#E6E6E6] px-4 py-3 flex items-center gap-3" data-testid="chat-header">
              <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setActiveConv(null)}>
                <ArrowLeft size={18} />
              </Button>
              <div className="relative shrink-0">
                <Avatar className="h-9 w-9 border border-[#E6E6E6]">
                  {otherAvatar ? <AvatarImage src={otherAvatar} /> : null}
                  <AvatarFallback className="bg-[#074572] text-white text-xs">{otherInitials}</AvatarFallback>
                </Avatar>
                {otherPresence.online && (
                  <span
                    className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-white rounded-full"
                    data-testid="chat-header-online-dot"
                  />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-zinc-900 truncate">{activeConv.other_user?.name}</p>
                <p className="text-[11px] text-zinc-400" data-testid="chat-header-presence">
                  {otherPresence.online ? (
                    <span className="text-green-600 font-medium">Active now</span>
                  ) : (
                    formatLastActive(otherPresence.last_active, otherPresence.online)
                  )}
                  {activeConv.other_user?.role && <span className="capitalize"> · {activeConv.other_user.role}</span>}
                </p>
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-3 max-w-2xl mx-auto">
                {messages.map(msg => {
                  const isMine = msg.sender_id === myId;
                  const seen = !!msg.seen_at || !!msg.read;
                  return (
                    <div key={msg.id} className={`flex ${isMine ? "justify-end" : "justify-start"}`} data-testid={`message-${msg.id}`}>
                      <div className={`max-w-[70%] px-4 py-2.5 rounded-2xl text-sm ${
                        isMine ? "bg-[#074572] text-white rounded-br-sm" : "bg-white border border-[#E6E6E6] text-zinc-800 rounded-bl-sm"
                      }`}>
                        {msg.content && <p className="whitespace-pre-wrap break-words">{msg.content}</p>}
                        <AttachmentPreview msg={msg} />
                        <div className={`flex items-center justify-end gap-1 mt-1 text-[10px] ${isMine ? "text-white/60" : "text-zinc-400"}`}>
                          <span>{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                          {isMine && (
                            seen ? (
                              <CheckCheck size={12} className="text-sky-300" data-testid={`msg-seen-${msg.id}`} />
                            ) : (
                              <Check size={12} data-testid={`msg-sent-${msg.id}`} />
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEnd} />
              </div>
            </ScrollArea>

            {/* Pending attachment preview */}
            {pendingAttach && (
              <div className="bg-white border-t border-[#E6E6E6] px-4 py-2 flex items-center gap-3" data-testid="pending-attachment">
                {pendingAttach.previewUrl ? (
                  <img src={pendingAttach.previewUrl} alt="preview" className="h-12 w-12 object-cover rounded-md border border-[#E6E6E6]" />
                ) : (
                  <div className="h-12 w-12 rounded-md bg-[#074572]/5 flex items-center justify-center">
                    <FileIcon size={18} className="text-[#074572]" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-zinc-800 truncate">{pendingAttach.name}</p>
                  <p className="text-[10px] text-zinc-400">{Math.round((pendingAttach.size || 0) / 1024)} KB · Ready to send</p>
                </div>
                <button onClick={clearPendingAttach} className="text-zinc-400 hover:text-[#F85037] p-1" data-testid="clear-attachment">
                  <X size={16} />
                </button>
              </div>
            )}

            {/* Input */}
            <div className="bg-white border-t border-[#E6E6E6] p-4" data-testid="chat-input-area">
              <div className="flex gap-2 max-w-2xl mx-auto items-center">
                <input
                  ref={imageInputRef}
                  type="file"
                  accept={IMAGE_ACCEPT}
                  className="hidden"
                  onChange={handleFileSelected}
                  data-testid="attach-image-input"
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ATTACH_ACCEPT}
                  className="hidden"
                  onChange={handleFileSelected}
                  data-testid="attach-file-input"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={uploading}
                  onClick={() => handlePickFile(IMAGE_ACCEPT)}
                  className="text-zinc-500 hover:text-[#074572] shrink-0"
                  aria-label="Attach image"
                  data-testid="attach-image-btn"
                >
                  <ImagePlus size={18} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={uploading}
                  onClick={() => handlePickFile(ATTACH_ACCEPT)}
                  className="text-zinc-500 hover:text-[#074572] shrink-0"
                  aria-label="Attach file"
                  data-testid="attach-file-btn"
                >
                  <Paperclip size={18} />
                </Button>
                <Input
                  value={newMsg} onChange={(e) => setNewMsg(e.target.value)}
                  placeholder={uploading ? "Uploading attachment..." : "Type a message..."}
                  disabled={uploading}
                  className="border-[#E6E6E6] focus:ring-[#074572]/20 focus:border-[#074572]"
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSend()}
                  data-testid="chat-message-input"
                />
                <Button
                  onClick={handleSend} disabled={(!newMsg.trim() && !pendingAttach) || sending || uploading}
                  className="bg-[#F85037] hover:bg-[#e04530] text-white shrink-0"
                  data-testid="chat-send-btn"
                >
                  <Send size={16} />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      <ComposeDialog
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        existingIds={conversations.map(c => c.other_user?._id).filter(Boolean)}
        onPick={(u) => {
          const uid = u._id || u.id;
          setComposeOpen(false);
          if (uid) navigate(`/messages/${uid}`);
        }}
      />
    </div>
  );
}
